

import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import apiClient from "../../api/axiosClient";
import { useNavigate } from "react-router-dom";

const TEMPLATE_FILENAME = "Oil Marketing_Award2025.xlsx";

const rowMapping = {
  organisationname: 3,


    revenue:5,
    volume:6,
    domestic_2024:11,
    domestic_2023:12,

    Ms_2024:17,
    Ms_2023:18,

    Hsd_2024:22,
    Hsd_2023:23,

    retail_outlets_2024:27,
    retail_outlets_2023:28,

    sales_2024:32,
    no_employees:33,

    sales_lubricant_2024:37,
    sales_lubricant_2023:38,

    tankage_ms_2024:43,
    tankage_ms_2023:44,

    Hsd_increase_2024:48,
    Hsd_increase_2023:49,

    Hsd_ethanol_2024:53,
    Hsd_ethanol_2023:54,

    Ros_2024:59,
    Total_Ros:60,

    Non_cash_2024:64,
    Non_cash_sales:65,

    
    Gps_2024:69,
    Gps_total_2024:70,

    complaints_2024:75,
    complaints_2023:76,

    turnaround_2024:80,
    turnaround_2023:81,

    fast_charging_2024:86,
    fast_charging_2023:87,

    h2_dispensing_2024:93,
    h2_dispensing_2023:94,

    cbg_sales_2024:100,
    cbg_sales_2023:101,


    lpg_2024:107,
    lpg_2023:108,

    capex_2024:114,
    Total_capex:115,

    ethanol_2024:119,
    ethanol_blending_2024:120,

    fatalities:124,





};

/** Helpers for Excel columns / range */
const colLetter = (colIndex) => {
  let letters = "";
  while (colIndex >= 0) {
    letters = String.fromCharCode((colIndex % 26) + 65) + letters;
    colIndex = Math.floor(colIndex / 26) - 1;
  }
  return letters;
};

const colIndexFromLetter = (col) => {
  let index = 0;
  for (let i = 0; i < col.length; i++) {
    index = index * 26 + (col.charCodeAt(i) - 65 + 1);
  }
  return index - 1;
};

const expandSheetRange = (sheet, minCol, minRow, maxCol, maxRow) => {
  const start = `${colLetter(minCol)}${minRow}`;
  const end = `${colLetter(maxCol)}${maxRow}`;
  sheet["!ref"] = `${start}:${end}`;
};

const ExcelOM = () => {
  const [apiData, setApiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exported, setExported] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiClient.get("registration-om/submitted/");
        const results = (resp.data && (resp.data.results ?? resp.data)) || [];
        console.log("API results length:", results.length);
        setApiData(results);
      } catch (err) {
        console.error("API fetch error:", err);
        setError("Failed to fetch API data. See console.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (!loading && !error && apiData && apiData.length > 0 && !exported) {
      (async () => {
        try {
          await performExport(apiData);
          setExported(true);
        } catch (err) {
          console.error("Auto-export failed:", err);
        }
      })();
    }
  }, [loading, error, apiData, exported]);

  // Helper that tries many field names
  const getVal = (obj, ...keys) => {
    for (const k of keys) {
      if (!k) continue;
      const val = obj[k];
      if (val !== undefined && val !== null) return val;
    }
    return undefined;
  };

  // resolve merges: if (r,c) lies inside a merged range, return top-left of that merge
  const resolveMergeCell = (sheet, r, c) => {
    const merges = sheet["!merges"] || [];
    for (const m of merges) {
      if (r >= m.s.r && r <= m.e.r && c >= m.s.c && c <= m.e.c) {
        return { r: m.s.r, c: m.s.c };
      }
    }
    return { r, c };
  };

  const performExport = async (dataToWrite) => {
    if (!dataToWrite || dataToWrite.length === 0) {
      throw new Error("No API data to export.");
    }

    const url = encodeURI(`${process.env.PUBLIC_URL || ""}/templates/${TEMPLATE_FILENAME}`);
    console.log("Fetching template:", url);
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`Template not found (status ${resp.status})`);

    const contentType = resp.headers.get("content-type") || "";
    if (contentType.includes("text/html")) {
      const txt = await resp.text();
      console.error("Template fetch returned HTML (first 300 chars):", txt.slice(0, 300));
      throw new Error("Template returned HTML. Check file path/name.");
    }

    const arrayBuffer = await resp.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const sheet = workbook.Sheets[sheetName];
    console.log("Using sheet:", sheetName, "initial !ref:", sheet["!ref"]);

    // existing bounds
    const existingRef = sheet["!ref"] || "A1:A1";
    const [startRef, endRef] = existingRef.split(":");
    const startColLetter = (startRef || "A1").replace(/[0-9]/g, "");
    const startRow = parseInt((startRef || "A1").replace(/[A-Z]/gi, ""), 10) || 1;
    const endColLetter = (endRef || startRef || "A1").replace(/[0-9]/g, "");
    const endRow = parseInt((endRef || startRef || "A1").replace(/[A-Z]/gi, ""), 10) || 1;

    let minCol = colIndexFromLetter(startColLetter || "A");
    let minRow = startRow;
    let maxCol = colIndexFromLetter(endColLetter || "A");
    let maxRow = endRow;

    // Choose start column (zero-based). Change START_COL if you want to begin at G (6) or somewhere else.
    const START_COL = 8; // 6 => column G. Adjust if your template expects a different starting column.

    dataToWrite.forEach((company, idx) => {
      const colIndex = START_COL + idx; // zero-based
      const col = colLetter(colIndex);
      console.log(`\n--- Writing company #${idx + 1} into column ${col} (zero-based ${colIndex}) ---`);
      console.log("Company raw keys:", Object.keys(company).slice(0,50)); // quick peek

      // attempt multiple key names for each field to be resilient to API changes
      const organisationname = getVal(company, "organisation_name", "organisationname", "org_name", "organisation");
      const revenue = getVal(company, "domestic_sales_revenue_2024", "domestic_volume_mmt_2024", "domestic_sales_revenue_2024");
      const volume = getVal(company, "domestic_volume_mmt_2024", "domestic_volume_mmt_2024");
      const domestic_2024 = getVal(company, "domestic_sales_revenue_2024");
      const domestic_2023 = getVal(company, "domestic_sales_revenue_2023", "domestic_sales_revenue_2023");
      const Ms_2024 = getVal(company, "retail_ms_2024");
      const Ms_2023 = getVal(company, "retail_ms_2023", "retail_ms_2023");
      const Hsd_2024 = getVal(company, "retail_hsd_2024");
      const Hsd_2023 = getVal(company, "retail_hsd_2023");
      const retail_outlets_2024 = getVal(company, "retail_outlets_2024");
      const retail_outlets_2023 = getVal(company, "retail_outlets_2023", "capex_other_2024");
      const sales_2024 = getVal(company, "sales_per_employee_total_2024", "export_sales_revenue_2024");
      const no_employees = getVal(company, "sales_per_employee_count_2024");
      const sales_lubricant_2024 = getVal(company, "lubricants_sales_2024");
      const sales_lubricant_2023 = getVal(company, "fuels_sales_2024");
      const tankage_ms_2024 = getVal(company, "tankage_ms_2024");
      const tankage_ms_2023 = getVal(company, "tankage_ms_2023");
     
     
     
     
      const Hsd_increase_2024 = getVal(company, "tankage_hsd_2024");
      const Hsd_increase_2023 = getVal(company, "tankage_hsd_2023");
      const Hsd_ethanol_2024 = getVal(company, "tankage_ethanol_2024");
      const Hsd_ethanol_2023 = getVal(company, "tankage_ethanol_2023");
      const Ros_2024 = getVal(company, "automated_ros_2024");
      const Total_Ros = getVal(company, "total_ros_2024");
      const Non_cash_2024 = getVal(company, "non_cash_sales_2024");
      const Non_cash_sales = getVal(company, "total_sales_2024");
      const Gps_total_2024 = getVal(company, "total_trucks_2024");
      const Gps_2024 = getVal(company, "gps_trucks_2024");
      const complaints_2024 = getVal(company, "complaints_number_2024");
      const complaints_2023 = getVal(company, "complaints_number_2023");
      const turnaround_2024 = getVal(company, "complaints_turnaround_2024");
      const turnaround_2023 = getVal(company, "complaints_turnaround_2023");
      const fast_charging_2024 = getVal(company, "ev_stations_2024");
      const fast_charging_2023 = getVal(company, "ev_stations_2023");
      const h2_dispensing_2024 = getVal(company, "h2_stations_2024");
      const h2_dispensing_2023 = getVal(company, "h2_stations_2023");
      const cbg_sales_2024 = getVal(company, "cbg_sales_2024");
      const cbg_sales_2023 = getVal(company, "cbg_sales_2023");
      
      const lpg_2024 = getVal(company, "lpg_consumption_2024");
      const lpg_2023 = getVal(company, "lpg_consumption_2023");
      
      const capex_2024 = getVal(company, "biofuels_investment_2024");
      const Total_capex = getVal(company, "total_capex_2024");

      const ethanol_2024 = getVal(company, "ethanol_blending_actual_2024");
      const ethanol_blending_2024 = getVal(company, "ethanol_blending_target_2024");
      const fatalities = getVal(company, "fatalities_2024");

      const writes = [
        [rowMapping.organisationname, organisationname, "organisationname"],
        [rowMapping.revenue, revenue, "revenue"],
        [rowMapping.volume, volume, "volume"],
        [rowMapping.domestic_2024, domestic_2024, "domestic_2024"],
        [rowMapping.domestic_2023, domestic_2023, "domestic_2023"],
        [rowMapping.Ms_2024, Ms_2024, "Ms_2024"],
        [rowMapping.Ms_2023, Ms_2023, "Ms_2023"],
        [rowMapping.Hsd_2024, Hsd_2024, "Hsd_2024"],
        [rowMapping.Hsd_2023, Hsd_2023, "Hsd_2023"],
        [rowMapping.retail_outlets_2023, retail_outlets_2023, "retail_outlets_2023"],
        [rowMapping.retail_outlets_2024, retail_outlets_2024, "retail_outlets_2024"],
        [rowMapping.no_employees, no_employees, "no_employees"],
        [rowMapping.sales_2024, sales_2024, "sales_2024"],
        [rowMapping.sales_lubricant_2024, sales_lubricant_2024, "sales_lubricant_2024"],
        [rowMapping.sales_lubricant_2023, sales_lubricant_2023, "sales_lubricant_2023"],
        [rowMapping.tankage_ms_2023, tankage_ms_2023, "tankage_ms_2023"],
        [rowMapping.tankage_ms_2024, tankage_ms_2024, "tankage_ms_2024"],
        [rowMapping.Hsd_increase_2024, Hsd_increase_2024, "Hsd_increase_2024"],
        [rowMapping.Hsd_increase_2023, Hsd_increase_2023, "Hsd_increase_2023"],
        [rowMapping.Hsd_ethanol_2024, Hsd_ethanol_2024, "Hsd_ethanol_2024"],
        [rowMapping.Hsd_ethanol_2023, Hsd_ethanol_2023, "Hsd_ethanol_2023"],
        [rowMapping.Total_Ros, Total_Ros, "Total_Ros"],
        [rowMapping.Ros_2024, Ros_2024, "Ros_2024"],
        [rowMapping.Non_cash_2024, Non_cash_2024, "Non_cash_2024"],
        [rowMapping.Non_cash_sales, Non_cash_sales, "Non_cash_sales"],
        [rowMapping.Gps_2024, Gps_2024, "Gps_2024"],
        [rowMapping.Gps_total_2024, Gps_total_2024, "Gps_total_2024"],
        [rowMapping.complaints_2024, complaints_2024, "complaints_2024"],
        [rowMapping.complaints_2023, complaints_2023, "complaints_2023"],
        [rowMapping.turnaround_2024, turnaround_2024, "turnaround_2024"],
        [rowMapping.turnaround_2023, turnaround_2023, "turnaround_2023"],
        [rowMapping.fast_charging_2024, fast_charging_2024, "fast_charging_2024"],
        [rowMapping.fast_charging_2023, fast_charging_2023, "fast_charging_2023"],
        [rowMapping.h2_dispensing_2024, h2_dispensing_2024, "h2_dispensing_2024"],
        [rowMapping.h2_dispensing_2023, h2_dispensing_2023, "h2_dispensing_2023"],
        [rowMapping.cbg_sales_2024, cbg_sales_2024, "cbg_sales_2024"],
        [rowMapping.cbg_sales_2023, cbg_sales_2023, "cbg_sales_2023"],
        [rowMapping.lpg_2024, lpg_2024, "lpg_2024"],
        [rowMapping.lpg_2023, lpg_2023, "lpg_2023"],
        [rowMapping.capex_2024, capex_2024, "capex_2024"],
        [rowMapping.Total_capex, Total_capex, "Total_capex"],
        [rowMapping.ethanol_2024, ethanol_2024, "ethanol_2024"],
        [rowMapping.ethanol_blending_2024, ethanol_blending_2024, "ethanol_blending_2024"],
        [rowMapping.fatalities, fatalities, "fatalities"],
      ];

      writes.forEach(([rowNum, value, fieldName]) => {
        if (rowNum == null) return; // missing mapping
        const rZero = rowNum - 1;
        const cZero = colIndex;

        // resolve merges (if writing inside a merged cell, write to master)
        const { r: rResolved, c: cResolved } = resolveMergeCell(sheet, rZero, cZero);

        // If value is undefined/null -> set to empty string but log it
        let writeVal = value;
        if (writeVal === undefined || writeVal === null || writeVal === "") {
          console.warn(`Company #${idx + 1} field "${fieldName}" missing -> writing empty string to ${colLetter(cResolved)}${rResolved + 1}`);
          writeVal = "";
        }

        // Try to coerce numeric-looking strings to numbers
        if (typeof writeVal === "string") {
          const trimmed = writeVal.trim();
          if (trimmed !== "" && /^-?\d+(\.\d+)?$/.test(trimmed)) {
            writeVal = Number(trimmed);
          }
        }

        // Use sheet_add_aoa with { origin: { r, c } } for robust placement
        try {
          XLSX.utils.sheet_add_aoa(sheet, [[writeVal]], { origin: { r: rResolved, c: cResolved } });
          const cellAddress = `${colLetter(cResolved)}${rResolved + 1}`;
          const newCell = sheet[cellAddress];
          if (newCell) {
            if (typeof writeVal === "number") newCell.t = "n";
            else if (typeof writeVal === "boolean") newCell.t = "b";
            else newCell.t = "s";
          }
          console.log(`WROTE ${fieldName} -> ${colLetter(cResolved)}${rResolved + 1} =`, writeVal);
        } catch (e) {
          console.error("Write failed for", fieldName, "at", rResolved, cResolved, e);
          // fallback: write raw cell object
          const fallbackCellRef = `${colLetter(cZero)}${rowNum}`;
          sheet[fallbackCellRef] = { t: typeof writeVal === "number" ? "n" : "s", v: writeVal };
        }

        if (cZero > maxCol) maxCol = cZero;
        if (rowNum > maxRow) maxRow = rowNum;
      });
    });

    // update sheet range so Excel shows new cells
    expandSheetRange(sheet, minCol, minRow, Math.max(maxCol, minCol), Math.max(maxRow, minRow));
    console.log("Updated sheet !ref:", sheet["!ref"]);

    const wbout = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `Modified_${TEMPLATE_FILENAME}`);
    console.log("Excel exported successfully.");
  };

  if (loading) return <div>Loading API data...</div>;
  if (error) return <div style={{ color: "red" }}>{error}</div>;
  return (
    <>
      {navigate("/Thankyou")}
    </>
  );
};

export default ExcelOM;
